import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { subscriptionAPI } from '../../lib/api';

export default function SubscriptionTermsPage() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    subscriptionAPI.getPlans()
      .then(res => {
        setPeriods(res.data?.pro?.periods || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getPeriod = (months) => periods?.find(p => p.period_months === months);
  const fmt = (n) => n != null ? n.toLocaleString('ru-RU') : '—';
  const periodName = (m) => m === 1 ? '1 месяц' : m === 3 ? '3 месяца' : m === 6 ? '6 месяцев' : '12 месяцев';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Назад
        </Button>

        <article className="prose prose-sm dark:prose-invert max-w-none">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            Оферта подписки
          </h1>
          <p className="text-muted-foreground text-sm mb-8">
            Последнее обновление: 10 июля 2025 г.
          </p>

          {/* 1. Общие положения */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              1. Общие положения
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Настоящая Оферта подписки (далее — «Оферта») является публичным предложением
              Индивидуального предпринимателя, администрации сервиса <strong>YaDela</strong> (далее — «Исполнитель»),
              адресованным любому физическому или юридическому лицу (далее — «Подписчик», «Клиент»),
              заключить договор на предоставление доступа к платной подписке «Про» сервиса YaDela
              на условиях единоразовой оплаты за выбранный период.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Акцептом (принятием) настоящей Оферты является установка отметки в чек-боксе
              «Согласен с условиями оферты подписки» на странице оформления подписки
              и осуществление платежа.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Настоящая Оферта является самостоятельным документом и действует отдельно от
              Пользовательского соглашения (общей оферты) сервиса YaDela.
            </p>
          </section>

          {/* 2. Предмет оферты */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              2. Предмет оферты
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Исполнитель предоставляет Подписчику доступ к расширенным функциям сервиса YaDela
              в рамках тарифного плана «Про», включающего:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Безлимитные заказы, задачи и клиенты;</li>
              <li>Telegram- и Email-уведомления;</li>
              <li>Полная аналитика, календарь и отчёты;</li>
              <li>Экспорт данных (CSV, ICS);</li>
              <li>Публичная страница мастера;</li>
              <li>Приоритетная поддержка.</li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Подписка оплачивается единоразово за выбранный период. Автоматическое списание
              денежных средств по окончании оплаченного периода <strong>не производится</strong>.
            </p>
          </section>

          {/* 3. Стоимость и периоды подписки */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              3. Стоимость и периоды подписки
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Подписчик выбирает один из доступных периодов подписки и оплачивает его
              единоразово. Чем длиннее период, тем больше скидка:
            </p>
            <div className="overflow-x-auto my-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
              <table className="w-full text-sm border border-border rounded-lg">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-3 border-b border-border font-semibold">Период подписки</th>
                    <th className="text-left p-3 border-b border-border font-semibold">Стоимость в месяц</th>
                    <th className="text-left p-3 border-b border-border font-semibold">Сумма оплаты за период</th>
                    <th className="text-left p-3 border-b border-border font-semibold">Скидка</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 3, 6, 12].map((m, i) => {
                    const p = getPeriod(m);
                    if (!p) return null;
                    return (
                      <tr key={m} className={i % 2 === 1 ? "bg-muted/20" : ""}>
                        <td className={`p-3 ${i < 3 ? 'border-b border-border' : ''}`}>{periodName(m)}</td>
                        <td className={`p-3 ${i < 3 ? 'border-b border-border' : ''}`}>
                          {fmt(p.price_per_month)} ₽
                        </td>
                        <td className={`p-3 ${i < 3 ? 'border-b border-border' : ''}`}>{fmt(p.total)} ₽</td>
                        <td className={`p-3 ${i < 3 ? 'border-b border-border' : ''}`}>
                          {p.discount_percent > 0 ? (
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">{p.discount_percent}%</span>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </div>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Исполнитель оставляет за собой право изменять стоимость подписки с уведомлением
              Подписчика не менее чем за 14 (четырнадцать) календарных дней до вступления
              новых цен в силу. Уведомление направляется на электронную почту, указанную
              при регистрации.
            </p>
          </section>

          {/* 4. Порядок оплаты */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              4. Порядок оплаты
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Оплата осуществляется банковской картой через платёжный сервис
              АО «Тинькофф Банк» (Т-Банк). Подписчик совершает единоразовый платёж
              за выбранный период.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Поддерживаемые платёжные системы: Visa, Mastercard, МИР.
              Все транзакции защищены технологией 3-D Secure.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Данные банковской карты не сохраняются на стороне сервиса YaDela.
              Обработка платежей осуществляется платёжным провайдером по стандарту PCI DSS.
            </p>
          </section>

          {/* 5. Срок действия подписки и продление */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              5. Срок действия подписки и продление
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Подписка действует в течение оплаченного периода. По окончании периода:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Подписка <strong>не продлевается автоматически</strong>;</li>
              <li>Автоматическое списание денежных средств <strong>не производится</strong>;</li>
              <li>Аккаунт автоматически переводится на бесплатный тариф «Демо» с ограничениями;</li>
              <li>Все данные Подписчика сохраняются.</li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Для продления подписки Подписчик самостоятельно оплачивает новый период
              на странице тарифов. За 1 день до окончания текущего периода Подписчик
              получает уведомление (email, Telegram, push — в зависимости от настроек).
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              При продлении до истечения текущей подписки новый период добавляется
              к оставшемуся сроку.
            </p>
          </section>

          {/* 6. Возврат денежных средств */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              6. Возврат денежных средств
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Возврат денежных средств осуществляется в следующих случаях:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>
                <strong>В течение 14 (четырнадцати) календарных дней</strong> с момента оплаты —
                полный возврат суммы платежа при условии, что услугами тарифа «Про»
                фактически не пользовались (не создавались заказы, задачи и клиенты сверх лимитов
                бесплатного тарифа);
              </li>
              <li>
                <strong>При технической неисправности сервиса</strong>, повлёкшей невозможность
                использования оплаченных функций более 5 (пяти) календарных дней подряд —
                возврат пропорционально неиспользованному периоду;
              </li>
              <li>
                <strong>Ошибочное или двойное списание</strong> — полный возврат излишне
                списанной суммы.
              </li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Для оформления возврата необходимо направить заявление на электронную почту
              <strong> Epifanofe@mail.ru</strong> с указанием:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Email аккаунта в сервисе YaDela;</li>
              <li>Дата и сумма платежа;</li>
              <li>Причина обращения.</li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Срок рассмотрения заявления на возврат — до 10 (десяти) рабочих дней.
              Возврат осуществляется на банковскую карту, с которой был произведён платёж,
              в течение 5–30 рабочих дней в зависимости от банка-эмитента карты.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Частичный возврат за неиспользованную часть оплаченного периода (кроме случаев,
              указанных выше) не производится.
            </p>
          </section>

          {/* 7. Ответственность сторон */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              7. Ответственность сторон
            </h2>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Исполнитель обязуется предоставить доступ к оплаченным функциям в полном объёме;</li>
              <li>Подписчик несёт ответственность за достоверность платёжных реквизитов и наличие
                  достаточных средств на карте;</li>
              <li>Исполнитель не несёт ответственности за сбои, вызванные действиями третьих лиц
                  (банков, платёжных систем, интернет-провайдеров).</li>
            </ul>
          </section>

          {/* 8. Срок действия и изменение оферты */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              8. Срок действия и изменение оферты
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Настоящая Оферта действует с момента её публикации на сайте сервиса YaDela
              и до момента её отзыва Исполнителем.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Исполнитель вправе вносить изменения в настоящую Оферту. Изменения вступают в силу
              с момента публикации новой редакции. Подписчики уведомляются об изменениях по
              электронной почте не менее чем за 14 дней до вступления изменений в силу.
            </p>
          </section>

          {/* 9. Применимое право */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              9. Применимое право и разрешение споров
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Настоящая Оферта регулируется законодательством Российской Федерации,
              включая Федеральный закон «О защите прав потребителей» и Гражданский
              кодекс Российской Федерации.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Все споры и разногласия подлежат урегулированию путём переговоров.
              В случае недостижения согласия споры рассматриваются в порядке,
              предусмотренном законодательством Российской Федерации.
            </p>
          </section>

          {/* 10. Контактная информация */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              10. Контактная информация
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              По всем вопросам, связанным с подпиской, оплатой и возвратом
              денежных средств, вы можете обратиться:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Электронная почта: <strong>Epifanofe@mail.ru</strong></li>
              <li>Через раздел «Поддержка» в сервисе YaDela</li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Время обработки обращений: в рабочие дни с 10:00 до 19:00 (МСК).
              Срок ответа — не более 2 (двух) рабочих дней.
            </p>
          </section>

          <div className="mt-10 p-4 rounded-lg bg-muted/50 border border-border">
            <p className="text-xs text-muted-foreground text-center">
              Оплачивая подписку и устанавливая отметку в чек-боксе согласия, вы подтверждаете,
              что ознакомились с настоящей Офертой и принимаете все её условия в полном объёме.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}
